import { useMemo, useState, type ReactNode } from 'react';
import { useTranslations } from 'use-intl';

import { RouterLinkAs } from '@/lib/router-link';
import { z } from 'zod';
import {
  Button,
  Combobox,
  I,
  type ComboboxOption,
  type TabBarItem,
} from '@leanin/ui';
import { TabbedListPage } from '../page-shell/index';
import {
  enumList,
  FilterButton,
  FilterGroup,
  FilterSheet,
  PillFilter,
  useFilterParams,
  AppliedFilters,
  buildChips,
} from '../filters/index';
import { useCircleLocations } from '../../lib/queries/groups';

// Canonical circle-format values — identical to the server enum
// (`circles.format`) and the `format` query param, so filter state, the URL,
// and the API all speak one language. Display strings are derived at render
// via `formatLabel` (i18n). Single source of truth: imported by the route and
// the list body rather than re-declared. Mirrors the events filter, which
// already uses canonical values.
export const CIRCLE_FORMATS = ['in_person', 'virtual', 'hybrid'] as const;
export type CircleFormat = (typeof CIRCLE_FORMATS)[number];

export type CirclesScope = 'all' | 'mine';

// The schema's transform fills in defaults; passing `signedIn=false`
// substitutes 'all' for the missing-scope case so anonymous viewers land on
// the All Circles tab. The factory keeps the two callsites (chrome + body) in
// sync without forking the type. Mirrors `makeNetworksFilterSchema`.
export function makeCirclesFilterSchema(signedIn: boolean) {
  const fallbackScope: CirclesScope = signedIn ? 'mine' : 'all';
  return z
    .object({
      q: z.string().optional(),
      scope: z.enum(['all', 'mine']).optional(),
      format: enumList(CIRCLE_FORMATS),
      placeText: z.string().optional(),
    })
    .transform((v) => ({
      q: v.q ?? '',
      scope: (v.scope ?? fallbackScope) as CirclesScope,
      format: v.format,
      placeText: v.placeText ?? '',
    }));
}

export const circlesFilterSchema = makeCirclesFilterSchema(true);

export type CirclesFilters = z.infer<typeof circlesFilterSchema>;

export const CIRCLES_FILTER_DEFAULTS: CirclesFilters = {
  q: '',
  scope: 'mine',
  format: [],
  placeText: '',
};

const toScope = (raw: string | undefined): CirclesScope =>
  raw === 'all' ? 'all' : 'mine';

type Props = {
  readonly children: ReactNode;
  /**
   * Whether the viewer has a session. When `false` the chrome drops the
   * "My Circles" tab and the "Start a Circle" CTA — both member-only — and
   * treats every visit as the All Circles directory. Defaults to `true` so
   * existing authed callsites are unchanged.
   */
  readonly signedIn?: boolean;
};

export function CirclesChrome({ children, signedIn = true }: Props) {
  const tc = useTranslations('Circles');
  // Translated display label for a format. The persisted option VALUE is the
  // canonical enum (`CIRCLE_FORMATS`), matching raw circle data and the API;
  // only the label is localized.
  const formatLabel = (v: CircleFormat): string =>
    v === 'virtual'
      ? tc('chrome.formatVirtual')
      : v === 'hybrid'
        ? tc('chrome.formatHybrid')
        : tc('chrome.formatInPerson');
  const { values, set, clear } = useFilterParams<CirclesFilters>({
    schema: makeCirclesFilterSchema(signedIn),
    defaults: signedIn
      ? CIRCLES_FILTER_DEFAULTS
      : { ...CIRCLES_FILTER_DEFAULTS, scope: 'all' as CirclesScope },
    debounce: { keys: ['q'] },
    resetKeys: [],
  });
  const tabs: TabBarItem[] = signedIn
    ? [
        { id: 'mine', label: tc('chrome.tabMine') },
        { id: 'all', label: tc('chrome.tabAll') },
      ]
    : [{ id: 'all', label: tc('chrome.tabAll') }];
  const [sheetOpen, setSheetOpen] = useState(false);

  // Distinct locations across existing circles, sorted by usage. Refetched
  // lazily; the combobox renders an empty list while it loads but the input
  // is still usable, and the staleTime in useCircleLocations means a cached
  // page-1 result is reused while the user retypes filters.
  const locationsQuery = useCircleLocations();
  const locationOptions = useMemo<ReadonlyArray<ComboboxOption>>(
    () =>
      (locationsQuery.data ?? []).map((row) => ({
        value: row.placeText,
        label: row.placeText,
        sublabel: tc('chrome.locationCircleCount', { count: row.count }),
      })),
    [locationsQuery.data, tc],
  );

  const facetCount =
    values.format.length +
    (values.placeText.trim().length > 0 ? 1 : 0);

  const chips = buildChips(values, CIRCLES_FILTER_DEFAULTS, set, {
    format: {
      label: (v) =>
        tc('chrome.chipFormat', {
          value: formatLabel(v as CircleFormat),
        }),
    },
    placeText: { label: (v) => tc('chrome.chipLocation', { value: v }) },
  });

  return (
    <TabbedListPage
      title={tc('chrome.title')}
      subtitle={tc('chrome.subtitle')}
      headerActions={
        signedIn ? (
          <Button href="/circles/new" linkAs={RouterLinkAs} variant="primary" size="md" icon={I.plus}>
            {tc('chrome.startCircle')}
          </Button>
        ) : undefined
      }
      search={{
        value: values.q,
        onChange: (e) => set({ q: e.target.value }),
        placeholder: tc('chrome.searchPlaceholder'),
      }}
      searchActions={
        <>
          <FilterButton
            count={facetCount}
            onClick={() => setSheetOpen(true)}
            aria-expanded={sheetOpen}
            aria-controls="circles-filter-sheet"
          />
          <FilterSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            title={tc('chrome.filterTitle')}
            values={values}
            emptyValues={CIRCLES_FILTER_DEFAULTS}
            onApply={(next) =>
              set({
                format: next.format,
                placeText: next.placeText.trim(),
              })
            }
          >
            {(state, setState) => (
              <>
                <FilterGroup label={tc('chrome.filterFormat')}>
                  <PillFilter
                    multi
                    value={state.format}
                    onChange={(v) =>
                      setState({ ...state, format: v as CirclesFilters['format'] })
                    }
                    options={CIRCLE_FORMATS.map((v) => ({ value: v, label: formatLabel(v) }))}
                  />
                </FilterGroup>
                <FilterGroup label={tc('chrome.filterLocation')} hint={tc('chrome.filterLocationHint')}>
                  <Combobox
                    options={locationOptions}
                    value={state.placeText === '' ? null : state.placeText}
                    onChange={(next) =>
                      setState({ ...state, placeText: next ?? '' })
                    }
                    ariaLabel={tc('chrome.locationAriaLabel')}
                    placeholder={tc('chrome.locationSearchPlaceholder')}
                    emptyLabel={tc('chrome.locationEmpty')}
                    noOptionsLabel={
                      locationsQuery.isLoading
                        ? tc('chrome.locationLoading')
                        : tc('chrome.locationNone')
                    }
                  />
                </FilterGroup>
              </>
            )}
          </FilterSheet>
        </>
      }
      appliedFilters={<AppliedFilters chips={chips} onClearAll={() => clear()} />}
      tabs={tabs}
      activeTab={values.scope}
      onTabChange={(id) => set({ scope: toScope(id) })}
    >
      {children}
    </TabbedListPage>
  );
}
